import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useHasLocationsToRender from '@rsos/hooks/useHasLocationsToRender';
import { selectAppLauncherApp } from '@rsos/reduxDataStore/src/features/appLauncher/appLauncherSlice';
import {
  AVIVE_APP_LAUNCHER_MAP_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import AviveNotesForm from './AviveNotesForm';
import AviveSearchForm from './AviveSearchForm';
import {
  openAviveRequestMapCard,
  reopenAviveRequestMapCard,
  searchByLocation,
  updateNotes,
} from './aviveSlice';
import { createPlaceholder } from './fixtures/avive-request-spatial';

/**
 * Hide the search placeholders for the Avive AED on the map.
 */
export const hidePlaceholders = () => {
  try {
    const spatialPlaceholder = createPlaceholder({
      key: 'avive_aed_placeholder',
      display: false,
    });
    window.__RSOS?.hermes?.handle(spatialPlaceholder);
  } catch {
    /* to be safe, suppress, no need to recover */
  }
};

/**
 * Container component for the AviveNotesForm/AviveSearchForm triggered by the
 * app launcher
 * @param {Function} onClose - Callback function for closing the form
 */
const AviveContainer = ({ onClose = () => {} }) => {
  const isNotesVisible = useSelector(state => state.avive.isNotesVisible);

  const dispatch = useDispatch();

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );

  const filteredCalls = useSelector(state =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );

  const currentCall = useSelector(state => {
    const selectedCallerID = state.emergencyCalls.selectedCallerID;
    const calls = callQueueSelector(
      state.emergencyCalls.callQueue,
      hasAniAliAccess,
    );
    return calls[selectedCallerID];
  });
  const { mostRecentLocation, mostRecentSecondaryLocation } =
    getMostRecentLocations(currentCall);

  const [callDetails, setCallDetails] = useState(null);
  useEffect(() => {
    const location = mostRecentLocation || mostRecentSecondaryLocation;
    if (location) {
      const initialPhone = location.caller_id;
      // the location object has addressObject
      const { addressObject: addr } = location;
      const initialAddress = `${addr?.home || ''} ${addr?.street || ''} ${
        addr?.city || ''
      }, ${addr?.region || ''} ${addr?.postal_code || ''}`;
      const initialCoordinate = [
        location?.longitude,
        location?.latitude,
      ].filter(Boolean);
      setCallDetails({ initialPhone, initialAddress, initialCoordinate });
    }
  }, [mostRecentLocation, mostRecentSecondaryLocation]);

  const hasLocationsToRender = useHasLocationsToRender();

  let currentLocation =
    (hasLocationsToRender && mostRecentLocation) || mostRecentSecondaryLocation;

  if (currentLocation) {
    const { latitude, longitude } = currentLocation;
    currentLocation = { latitude, longitude };
  }

  const handleSearchSubmit = async (data, done = () => {}) => {
    // For standalone searches, subscribe to the phone number via Hermes so
    // that Avive ADR can be received. We know it's standalone if the form
    // has not been pre-populated with call data (callDetails).
    if (!callDetails?.initialPhone && data?.phone) {
      window.__RSOS?.hermes?.instance?.subscribe(`tel://${data.phone}`);
    }

    dispatch(openAviveRequestMapCard(data));

    // Center the map on the lat/long for the avive request object
    const latitude = data?.latitude;
    const longitude = data?.longitude;

    panMapTo({
      mapProvider: window.__RSOS?.map?.mapProvider,
      mapInstance: window.__RSOS?.map?.mapInstance,
      latitude: latitude,
      longitude: longitude,
      zoom: 16,
    });

    let action;
    const notes = data?.notes || undefined;

    if (latitude && longitude) {
      action = await dispatch(
        searchByLocation({
          phone: data?.phone,
          location: { latitude, longitude },
          notes,
          isStandaloneSearch: filteredCalls && !filteredCalls[data?.phone],
        }),
      );
    }

    if (action && !action.error) {
      hidePlaceholders();
    }

    done();
  };

  const handleFormClose = () => {
    hidePlaceholders();
    onClose();
  };

  const handleNotesSubmit = async (values, done) => {
    const action = await dispatch(
      updateNotes({
        phone: values.phone,
        notes: values.notes,
      }),
    );
    const hasError = !!action?.error;
    if (!hasError) {
      dispatch(selectAppLauncherApp(null));
      dispatch(closeCard(AVIVE_APP_LAUNCHER_MAP_CARD));
    }
    done(hasError);

    dispatch(reopenAviveRequestMapCard(values?.phone));
  };

  return (
    <>
      {isNotesVisible ? (
        <AviveNotesForm
          onClose={handleFormClose}
          onSubmit={handleNotesSubmit}
        />
      ) : (
        <AviveSearchForm
          initialPhone={callDetails?.initialPhone}
          initialAddress={callDetails?.initialAddress}
          initialCoordinate={callDetails?.initialCoordinate}
          currentLocation={currentLocation}
          onClose={handleFormClose}
          onSubmit={handleSearchSubmit}
        />
      )}
    </>
  );
};

export default AviveContainer;
